import { useState } from "react";
import api, { errMsg, openBlob } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, StateBadge, ReadMore, fmt, inputCls, btnPrimary, btnOutline, btnLink, Empty } from "@/components/portal/PortalUI";
import { Eye, Download, PenLine, CheckSquare, FileCheck2, Upload } from "lucide-react";

const SignDialog = ({ doc, student, onClose, reload }) => {
  const [name, setName] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const sign = async () => {
    setBusy(true);
    try { await api.post(`/student/documents/${doc.assignment_id}/sign`, { legal_name: name, consent }); toast.success("Document signed. A signed copy is available to download."); reload(); onClose(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="sign-dialog">
        <DialogHeader><DialogTitle className="font-display text-navy">Electronically Sign: {doc.title}</DialogTitle></DialogHeader>
        <p className="text-sm text-dark-text/70">Version {doc.version}. Type your full legal name exactly as it appears on your ID.</p>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder={student.legal_name || "Full legal name"} className={inputCls} data-testid="sign-legal-name" />
        {name && <p className="font-serif italic text-3xl text-magenta border-b border-navy/20 pb-2" data-testid="sign-preview">{name}</p>}
        <label className="flex gap-2 text-sm text-navy cursor-pointer">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="accent-magenta mt-1" data-testid="sign-consent" />
          I have reviewed this document and agree that typing my full legal name and checking this box is my legally binding electronic signature.
        </label>
        <button disabled={busy || !consent || name.trim().length < 3} onClick={sign} className={`${btnPrimary} w-full`} data-testid="sign-submit">{busy ? "Signing…" : "Sign Document"}</button>
      </DialogContent>
    </Dialog>
  );
};

const DocRow = ({ d, onSign, reload }) => {
  const act = async (path, msg) => { try { await api.post(`/student/documents/${d.assignment_id}/${path}`); toast.success(msg); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const view = async () => { try { await openBlob(`/student/documents/${d.assignment_id}/file`); setTimeout(reload, 800); } catch (e) { toast.error("Could not open document"); } };
  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/student/documents/${d.assignment_id}/upload`, fd); toast.success("File uploaded"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="border border-navy/10 rounded-lg p-4" data-testid={`student-doc-${d.assignment_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="font-display font-bold text-navy">{d.title}</h4>
            <span className="text-xs text-dark-text/50">v{d.version} · {d.category}</span>
            {d.required && <span className="text-[10px] font-bold text-magenta uppercase">Required</span>}
          </div>
          <ReadMore summary={d.summary} description={d.description} testid={`doc-readmore-${d.assignment_id}`} />
          <p className="text-xs text-dark-text/50 mt-2">Assigned {fmt(d.assigned_at)} · Due {fmt(d.due_date)} · Status: <span className="font-semibold text-navy" data-testid={`doc-status-${d.assignment_id}`}>{d.status}</span>
            {d.signature && <> · Signed {fmt(d.signature.signed_at)} as “{d.signature.legal_name}”</>}</p>
        </div>
        <StateBadge state={d.state} testid={`doc-state-${d.assignment_id}`} />
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        {d.version_file && <button onClick={view} className={btnOutline} data-testid={`doc-view-${d.assignment_id}`}><Eye size={15} /> Open & Review</button>}
        {d.version_file && d.downloadable && <button onClick={() => openBlob(`/student/documents/${d.assignment_id}/file`, { download: true, filename: d.version_file.filename, params: { download: true } })} className={btnLink}><Download size={14} /> Download</button>}
        {!d.version_file && !d.reviewed_at && <button onClick={() => act("review", "Marked as reviewed")} className={btnOutline} data-testid={`doc-review-${d.assignment_id}`}><Eye size={15} /> Mark Reviewed</button>}
        {d.requires_ack && !d.acknowledged_at && <button onClick={() => act("acknowledge", "Acknowledged")} className={btnOutline} data-testid={`doc-ack-${d.assignment_id}`}><CheckSquare size={15} /> I Acknowledge</button>}
        {d.requires_signature && !d.signature && <button onClick={() => onSign(d)} className={btnPrimary} data-testid={`doc-sign-${d.assignment_id}`}><PenLine size={15} /> Sign</button>}
        {d.requires_upload && (
          <label className={`${btnOutline} cursor-pointer`} data-testid={`doc-upload-${d.assignment_id}`}><Upload size={15} /> {d.upload ? "Replace upload" : "Upload file"}
            <input type="file" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} /></label>
        )}
        {d.signed_pdf && <button onClick={() => openBlob(`/student/documents/${d.assignment_id}/signed`, { download: true, filename: `Signed - ${d.title}.pdf` })} className={btnPrimary} data-testid={`doc-signed-download-${d.assignment_id}`}><FileCheck2 size={15} /> Download Signed Copy</button>}
      </div>
    </div>
  );
};

export const StudentDocuments = ({ docs, reload, student }) => {
  const [signing, setSigning] = useState(null);
  const open = docs.filter((d) => !d.completed_at);
  const done = docs.filter((d) => d.completed_at);
  return (
    <div className="space-y-6" data-testid="student-documents">
      <Card title={`Documents Requiring Action (${open.length})`}>
        {open.length === 0 ? <Empty>All assigned documents are complete.</Empty> : <div className="space-y-3">{open.map((d) => <DocRow key={d.assignment_id} d={d} onSign={setSigning} reload={reload} />)}</div>}
      </Card>
      <Card title={`Completed & Signed Documents (${done.length})`}>
        {done.length === 0 ? <Empty>No completed documents yet.</Empty> : <div className="space-y-3">{done.map((d) => <DocRow key={d.assignment_id} d={d} onSign={setSigning} reload={reload} />)}</div>}
      </Card>
      {signing && <SignDialog doc={signing} student={student} onClose={() => setSigning(null)} reload={reload} />}
    </div>
  );
};
